import { BaseService } from '@/services/api/base.service';
import type { ServiceResponse } from '@/services/api/base.service';

export interface CreatePaymentParams {
  clubId: string;
  coachId?: string;      // If present, payment routes to coach's Stripe account (100% to coach)
  amount: number;        // Amount in cents (e.g., 5000 = €50.00)
  currency?: string;     // Default: EUR
  description: string;
  metadata?: Record<string, string>;
  applicationFeePercent?: number;  // Platform fee percentage — always 0 for coach payments
}

export interface PaymentIntentResult {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  currency: string;
}

/**
 * Stripe environment configuration
 * SECURITY: Validates that the correct keys are used in each environment
 */
export const STRIPE_CONFIG = {
  /**
   * Check if we're using test mode keys
   * In production, this should return false
   */
  isTestMode(): boolean {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
    return publishableKey.startsWith('pk_test_');
  },

  /**
   * Check if Stripe is properly configured
   */
  isConfigured(): boolean {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    return !!publishableKey && (
      publishableKey.startsWith('pk_test_') ||
      publishableKey.startsWith('pk_live_')
    );
  },

  /**
   * Warn in production if using test keys
   * Call this on app startup
   */
  validateEnvironment(): void {
    if (import.meta.env.PROD && this.isTestMode()) {
      console.warn(
        '[STRIPE SECURITY WARNING] Using test mode Stripe keys in production environment. ' +
        'Set VITE_STRIPE_PUBLISHABLE_KEY to a pk_live_ key for real payments.'
      );
    }
  },

  /**
   * Get the publishable key
   */
  getPublishableKey(): string | undefined {
    return import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  }
} as const;

/**
 * Service for creating Stripe payments via Edge Function
 * Uses destination charges to send money directly to connected accounts
 */
export class StripePaymentService extends BaseService {
  /**
   * Create a payment intent for a club
   * Money goes directly to the club's connected Stripe account
   *
   * @param params - Payment parameters
   * @returns PaymentIntent client_secret for Stripe.js
   */
  async createPaymentIntent(
    params: CreatePaymentParams
  ): Promise<ServiceResponse<PaymentIntentResult>> {
    return this.withErrorHandling(async () => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/stripe-create-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify(params),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create payment');
      }

      return {
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId,
        amount: data.amount,
        currency: data.currency,
      };
    }, { operation: 'stripe-create-payment' });
  }

  /**
   * Helper to format amount in cents to display string
   */
  formatAmount(amountCents: number, currency: string = 'EUR'): string {
    const amount = amountCents / 100;
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  }

  /**
   * Helper to convert display amount to cents
   */
  toCents(amount: number): number {
    return Math.round(amount * 100);
  }
}

// Export singleton instance
export const stripePaymentService = new StripePaymentService();
